// Derivaciones puras del encabezado de detalle de un crédito (Cartera y Cobranza).
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { daysBetweenDays, formatShortDayMx } from '@/lib/dateHelpers';
import { arrearsDaysOf } from '@/lib/installments';
import { creditStatusPresentationOf, type CreditTone } from './logic';

export interface HeaderStatusPill {
    label: string;
    tone: CreditTone;
    title?: string;
}

/** Estados en los que «al corriente» o «N días de mora» dicen algo del cliente. */
const LIVE_STATUSES: readonly string[] = [
    LoanCreditStatusEnum.ACTIVE,
    LoanCreditStatusEnum.IN_ARREARS,
];

const CURRENT_LABEL = creditStatusPresentationOf(LoanCreditStatusEnum.ACTIVE).label;

/** Días de mora del crédito: los del silo; el plan solo los suple si el silo no los publica. */
export function resolveArrearsDays(
    overdueDays: number | null | undefined,
    plan: readonly LoanInstallmentResponse[] | undefined,
    today: string,
): number | null {
    if (overdueDays !== undefined && overdueDays !== null) return overdueDays;
    return plan ? arrearsDaysOf(plan, today) : null;
}

/** «N días de mora», con el singular bien dicho. */
export function arrearsDaysLabel(days: number): string {
    return days === 1 ? '1 día de mora' : `${String(days)} días de mora`;
}

/** La pastilla de estado: con mora, en rojo y por días; al día, «Activo · al corriente». */
export function creditStatusPillOf(status: string, arrearsDays: number | null): HeaderStatusPill {
    const presentation = creditStatusPresentationOf(status);
    const live = LIVE_STATUSES.includes(status);
    if (live && arrearsDays !== null && arrearsDays > 0) {
        return { label: arrearsDaysLabel(arrearsDays), tone: 'danger', title: presentation.label };
    }
    if (live && arrearsDays === 0) {
        return { label: `${CURRENT_LABEL} · al corriente`, tone: 'success' };
    }
    return { label: presentation.label, tone: presentation.tone };
}

/** «vence 05/oct · en 4 días», «vence hoy» o «venció 05/oct · hace 3 días». */
export function dueLineOf(dueDay: string, today: string): string {
    const date = formatShortDayMx(dueDay);
    const days = daysBetweenDays(dueDay, today);
    if (days === 0) return `vence hoy · ${date}`;
    if (days > 0) return `vence ${date} · en ${days === 1 ? '1 día' : `${String(days)} días`}`;
    const late = -days;
    return `venció ${date} · hace ${late === 1 ? '1 día' : `${String(late)} días`}`;
}

/** «candado bloqueado»: el estado del candado en minúscula para la sub-línea del equipo. */
export function lockLineOf(lockLabel: string): string {
    return `candado ${lockLabel.charAt(0).toLowerCase()}${lockLabel.slice(1)}`;
}
